import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../shared/services/language.services';
import { Store } from '@ngrx/store';
import {
  getUserSelector,
  isAuthenticatedSelector,
  retryLoginErrorSelector
} from '../../../user/store/user.selectors';
import { Observable } from 'rxjs';
import { IUser } from '../../../user/model/user.model';
import { retryLogin } from '../../../user/store/user.actions';
import { SsoService } from '../../../user/services/sso.service';

@Component({
    selector: 'nav-bar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.scss'],
    imports: [
        RouterModule,
        TranslateModule,
        CommonModule
    ],
    providers: [LanguageService]
})
export class NavbarComponent {
  languageDescription!: string;

  private languageService = inject(LanguageService);
  private translate = inject(TranslateService);
  private store = inject(Store);
  private router = inject(Router);
  private sso = inject(SsoService);
  private host = inject(ElementRef<HTMLElement>);

  isAuthenticated$: Observable<boolean> =
    this.store.select(isAuthenticatedSelector);

  retryLoginError$: Observable<string | undefined> =
    this.store.select(retryLoginErrorSelector);

  user$: Observable<IUser> =
    this.store.select(getUserSelector);

  isCollapsed = false;
  isMobileOpen = false;
  openMenu: string | null = null;
  openFooterMenu: 'language' | 'user' | null = null;

  ngOnInit(): void {
    this.languageService.setLanguage();
    this.isCollapsed = localStorage.getItem('sidebarCollapsed') === 'true';

    this.isAuthenticated$.subscribe(isAuthenticated => {
      if (!isAuthenticated && !this.isPublicOfferRoute(this.router.url)) {
        this.store.dispatch(retryLogin());
      }
    });

    this.retryLoginError$.subscribe(retryError => {
      if (retryError && !this.isPublicOfferRoute(this.router.url)) {
        this.router.navigate(['/']);
      }
    });

    this.setLanguageDescription(this.languageService.getLanguage() ?? 'en');
  }

  useLanguage(language: string): void {
    this.translate.use(language);
    this.translate.setDefaultLang(language);
    this.setLanguageDescription(language);
    this.closeFooterMenu();
    localStorage.setItem('language', language);
    window.location.reload();
  }

  setLanguageDescription(language: string): void {
    const currentLanguage =
      this.languageService.getLanguage() ?? language ?? 'en';

    switch (currentLanguage) {
      case 'en':
        this.languageDescription = 'English';
        break;
      case 'pt-BR':
        this.languageDescription = 'Português (BR)';
        break;
      case 'pt-PT':
        this.languageDescription = 'Português (PT)';
        break;
      default:
        this.languageDescription = 'English';
    }
  }

  getLanguageFlag(language: string): string {
    const currentLanguage =
      language || this.languageService.getLanguage() || 'en';

    switch (currentLanguage) {
      case 'en':
        return 'fi fi-us';
      case 'pt-BR':
        return 'fi fi-br';
      case 'pt':
      case 'pt-PT':
        return 'fi fi-pt';
      default:
        return 'fi fi-us';
    }
  }

  goToCalipoli(): void {
    this.sso.getSsoLink().subscribe(res => {
      window.location.href = res.url;
    });
  }



  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
    this.closeFooterMenu();
    localStorage.setItem('sidebarCollapsed', String(this.isCollapsed));
  }

  toggleMobile(): void {
    this.isMobileOpen = !this.isMobileOpen;
  }

  closeMobile(): void {
    this.isMobileOpen = false;
    this.closeFooterMenu();
  }

  toggleMenu(menu: string): void {
    this.openMenu = this.openMenu === menu ? null : menu;
    this.closeFooterMenu();
  }

  toggleFooterMenu(menu: 'language' | 'user'): void {
    this.openFooterMenu = this.openFooterMenu === menu ? null : menu;
  }

  closeFooterMenu(): void {
    this.openFooterMenu = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;

    if (target && !this.host.nativeElement.contains(target)) {
      this.closeFooterMenu();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapePress(): void {
    this.closeFooterMenu();
    this.isMobileOpen = false;
  }

  private isPublicOfferRoute(url: string): boolean {
    const normalizedUrl = (url ?? '').split('?')[0].split('#')[0];
    let decodedUrl = normalizedUrl;

    try {
      decodedUrl = decodeURIComponent(normalizedUrl);
    } catch {
      decodedUrl = normalizedUrl;
    }

    const isDigitalProductRoute = /^\/@[^/]+\/[^/]+$/.test(decodedUrl);
    const isSubscriptionRoute = /^\/@[^/]+\/subscriptions?\/[^/]+$/.test(decodedUrl);
    return isDigitalProductRoute || isSubscriptionRoute;
  }

}
